const SITE_NAME = 'AVEM';
const BASE_URL = 'https://avembg.com';
const DEFAULT_IMAGE = `${BASE_URL}/img/tape_dispenser_new.jpg`;

const I18N_DEFAULTS = {
    bg: {
        title: 'Компактен диспенсър за тиксо AVEM – Революция в опаковането | Произведено в България',
        description: 'Запознайте се с AVEM – най-лекия, компактен и екологичен диспенсър за тиксо на пазара! Монтира се директно върху ролката (15 g). Спестете време, място и разходи за опаковане.',
        keywords: 'диспенсър за тиксо, машинка за тиксо, компактен диспенсър за тиксо, тиксо машинка, резачка за тиксо, опаковъчно тиксо, супер прозрачно тиксо, диспенсър склад, авем, avem',
        ogTitle: 'Компактен диспенсър за тиксо AVEM – Революция в опаковането',
        ogDescription: 'Ултралек (15g) и икономичен диспенсър за тиксо AVEM. Перфектен за куриери, майстори, офиси и складове.',
        locale: 'bg_BG'
    },
    en: {
        title: 'Compact Tape Dispenser AVEM – Packaging Revolution | Made in Bulgaria',
        description: 'Meet AVEM – the lightest, most compact and eco-friendly tape dispenser on the market! Mounts directly onto the roll (15g). Save time, space, and packing expenses.',
        keywords: 'tape dispenser, compact tape dispenser, packing tape dispenser, packaging cutter, lightweight tape gun, clear tape, packaging warehouse, avem',
        ogTitle: 'Compact Tape Dispenser AVEM – Packaging Revolution',
        ogDescription: 'Ultra-light (15g) and cost-effective AVEM tape dispenser. Perfect for couriers, craftsmen, offices, and warehouses.',
        locale: 'en_US'
    }
};

/**
 * Gets currently selected language ('bg' or 'en')
 */
export function getCurrentLanguage() {
    try {
        return localStorage.getItem('languageCode') || 'bg';
    } catch (e) {
        return 'bg';
    }
}

/**
 * Retrieves cached terms dictionary from localStorage
 */
function getCachedTerms() {
    try {
        const raw = localStorage.getItem('terms');
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        return {};
    }
}

/**
 * Resolves localized string: checks terms dict first, then lang object, then fallback.
 */
function resolveI18n(value, lang, termsKey) {
    const terms = getCachedTerms();
    if (termsKey && terms[termsKey]) {
        return terms[termsKey];
    }
    if (value && typeof value === 'object') {
        return value[lang] || value.bg || value.en || '';
    }
    if (typeof value === 'string' && value.trim()) {
        return value;
    }
    const defaults = I18N_DEFAULTS[lang] || I18N_DEFAULTS.bg;
    return defaults[termsKey] || '';
}

/**
 * Updates or creates a <meta> tag by name or property attribute.
 */
function setMetaTag(attribute, key, content) {
    if (!content) return;
    let element = document.querySelector(`meta[${attribute}="${key}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
}

/**
 * Updates or creates a <link rel="..."> tag.
 */
function setLinkTag(rel, href) {
    if (!href) return;
    let element = document.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
}

/**
 * Updates document head SEO elements dynamically based on route and active language.
 */
export function updateRouteSeo(to, explicitLang) {
    const lang = explicitLang || getCurrentLanguage();
    const defaults = I18N_DEFAULTS[lang] || I18N_DEFAULTS.bg;
    const meta = (to && to.meta) || {};
    const routeName = (to && to.name) || 'home';

    // 0. Set HTML lang attribute
    if (document.documentElement) {
        document.documentElement.lang = lang;
    }

    // 1. Document Title
    const title = resolveI18n(meta.title, lang, `meta_${routeName}_title`) || defaults.title;
    document.title = title;

    // 2. Standard Meta Tags
    const description = resolveI18n(meta.description, lang, `meta_${routeName}_description`) || defaults.description;
    setMetaTag('name', 'description', description);

    const keywords = resolveI18n(meta.keywords, lang, `meta_${routeName}_keywords`) || defaults.keywords;
    setMetaTag('name', 'keywords', keywords);

    const robots = meta.robots || 'index, follow';
    setMetaTag('name', 'robots', robots);

    // 3. Canonical Link
    const path = (to && to.path) ? (to.path === '/' ? '/' : to.path) : '/';
    const canonical = meta.canonical || `${BASE_URL}${path}`;
    setLinkTag('canonical', canonical);

    // 4. Open Graph Tags
    const ogTitle = resolveI18n(meta.ogTitle, lang, `meta_${routeName}_og_title`) || title;
    const ogDescription = resolveI18n(meta.ogDescription, lang, `meta_${routeName}_og_description`) || description;
    setMetaTag('property', 'og:title', ogTitle);
    setMetaTag('property', 'og:description', ogDescription);
    setMetaTag('property', 'og:url', canonical);
    setMetaTag('property', 'og:image', meta.image || DEFAULT_IMAGE);
    setMetaTag('property', 'og:site_name', SITE_NAME);
    setMetaTag('property', 'og:locale', defaults.locale);

    // 5. Twitter Card Tags
    setMetaTag('name', 'twitter:title', ogTitle);
    setMetaTag('name', 'twitter:description', ogDescription);
    setMetaTag('name', 'twitter:image', meta.image || DEFAULT_IMAGE);
}

/**
 * Injects or updates dynamic Schema.org Product structured data
 */
export function updateProductJsonLd(product, explicitLang) {
    if (!product) return;
    const lang = explicitLang || getCurrentLanguage();
    let script = document.getElementById('dynamic-product-jsonld');
    if (!script) {
        script = document.createElement('script');
        script.id = 'dynamic-product-jsonld';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
    }
    const isEn = lang === 'en';
    const name = isEn ? (product.header_en || product.header) : (product.header || 'Ръчна машинка за пакетиране AVEM');
    const description = isEn
        ? (product.desc_en || product.short_en || 'Ultra lightweight and compact adhesive tape dispenser, manufactured in Bulgaria.')
        : (product.description || product.short || 'Ултра лек и компактен диспенсър за опаковъчно тиксо, произведен в България.');

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': name,
        'image': product.imageSrc ? (product.imageSrc.startsWith('http') ? product.imageSrc : `${BASE_URL}${product.imageSrc}`) : `${BASE_URL}/img/tape_dispenser_new.jpg`,
        'description': description,
        'brand': {
            '@type': 'Brand',
            'name': 'AVEM'
        },
        'offers': {
            '@type': 'Offer',
            'url': `${BASE_URL}/offers?product=${product.id}`,
            'priceCurrency': 'EUR',
            'price': product.priceEuro ? String(Number(product.priceEuro).toFixed(2)) : '3.53',
            'availability': 'https://schema.org/InStock',
            'itemCondition': 'https://schema.org/NewCondition'
        }
    };
    script.textContent = JSON.stringify(schema);
}
